import React, { useMemo } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { createLucideIcon, Loader2, SquareDashed } from 'lucide-react';

import { SubstitutionRuleActionsMenu } from 'components/fbm/substitutions/SubstitutionRuleActionsMenu';
import { SubstitutionRuleEnabledStatusCell } from 'components/fbm/substitutions/SubstitutionRuleEnabledStatusCell';
import { Badge } from 'shadcn/components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shadcn/components/ui/Table';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { cn } from 'shadcn/lib/utils';

// SquareOff is not available in the project's lucide-react version; paths match lucide's square-off icon.
const SquareOff = createLucideIcon('SquareOff', [
    ['path', { d: 'M20.4 20.4a2 2 0 01-1.4.6H5a2 2 0 01-2-2V5a2 2 0 01.59-1.41', key: '1' }],
    ['path', { d: 'M21 15.3V5a2 2 0 00-2-2H8.7', key: '2' }],
    ['path', { d: 'M22 22 2 2', key: '3' }],
]);

const renderEnabledStatusCell = ({ getValue }) => <SubstitutionRuleEnabledStatusCell isEnabled={getValue()} />;

/**
 * @param {number | null | undefined} value
 * @returns {string}
 */
export const getOnHandUnitsTextClassName = (value) => {
    if (value === 0) {
        return 'text-red-600';
    }
    if (value < 50) {
        return 'text-yellow-500';
    }
    return 'text-green-600';
};

/**
 * @param {Object} params
 * @param {boolean} params.hasSlot
 * @param {boolean} params.isEnabled
 * @param {boolean} params.isOutOfStock
 * @returns {'warning' | 'softDestructive' | 'neutral' | null}
 */
export const getSubstituteSlotBadgeVariant = ({ hasSlot, isEnabled, isOutOfStock }) => {
    if (hasSlot && !isOutOfStock) {
        return null;
    }

    if (!isEnabled) {
        return 'neutral';
    }

    if (!hasSlot) {
        return 'warning';
    }

    return 'softDestructive';
};

const renderOnHandUnits = (value) => {
    const units = value ?? 0;
    return <span className={getOnHandUnitsTextClassName(units)}>{units}</span>;
};

/**
 * @param {Object} params
 * @param {number | null | undefined} params.slotNumber
 * @param {string | null | undefined} params.slotStatus
 * @param {'warning' | 'softDestructive' | 'neutral' | null} params.badgeVariant
 * @returns {React.ReactNode}
 */
const renderSlotContent = ({ badgeVariant, slotNumber, slotStatus }) => {
    const hasSlot = slotNumber != null;
    const isOutOfStock = slotStatus === 'OUT_OF_STOCK';

    if (!hasSlot) {
        return (
            <Badge variant={badgeVariant}>
                <SquareDashed aria-label="empty" />
                none
            </Badge>
        );
    }

    if (isOutOfStock) {
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <span className="inline-flex">
                        <Badge variant={badgeVariant}>
                            <SquareOff aria-label="out of stock" />
                            {slotNumber}
                        </Badge>
                    </span>
                </TooltipTrigger>
                <TooltipContent>Slot Status = Out of Stock</TooltipContent>
            </Tooltip>
        );
    }

    return slotNumber;
};

const renderOriginalSlotCell = ({ row }) => {
    const slotNumber = row.original?.originalSlotNumber;
    const slotStatus = row.original?.originalSlotStatus;

    return renderSlotContent({
        badgeVariant: 'neutral',
        slotNumber,
        slotStatus,
    });
};

const renderSubstitutesCell = ({ getValue, row }) => {
    const substitutes = getValue();
    const isEnabled = row.original?.isEnabled !== false;

    if (!Array.isArray(substitutes) || substitutes.length === 0) {
        return <span className="text-muted-foreground">—</span>;
    }

    return (
        <Table className="overflow-hidden rounded-md border border-border bg-muted/30 text-xs [&_table]:table-fixed">
            <TableHeader>
                <TableRow className="hover:bg-transparent">
                    <TableHead className="h-8 w-16 px-2 py-1 text-center">Ext Sku</TableHead>
                    <TableHead className="h-8 px-2 py-1 text-left">Name</TableHead>
                    <TableHead className="h-8 w-10 px-2 py-1 text-center">Qty</TableHead>
                    <TableHead className="h-8 w-28 px-2 py-1 text-center">Slot</TableHead>
                    <TableHead className="h-8 w-20 px-2 py-1 pr-3 text-center">On Hand</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {substitutes.map((substitute) => {
                    const slotNumber = substitute?.substituteSlotNumber;
                    const hasSlot = slotNumber != null;
                    const isOutOfStock = substitute?.substituteSlotStatus === 'OUT_OF_STOCK';
                    const name = substitute?.substituteName ?? '—';
                    const badgeVariant = getSubstituteSlotBadgeVariant({ hasSlot, isEnabled, isOutOfStock });

                    return (
                        <TableRow
                            key={`${substitute?.substituteExternalSku ?? 'sku'}-${substitute?.substituteName ?? 'name'}`}
                        >
                            <TableCell className="w-16 px-2 py-1 text-center whitespace-nowrap">
                                {substitute?.substituteExternalSku ?? '—'}
                            </TableCell>
                            <TableCell className="px-2 py-1 whitespace-normal break-words">{name}</TableCell>
                            <TableCell className="w-10 px-2 py-1 text-center whitespace-nowrap">
                                {substitute?.substituteQuantity ?? '—'}
                            </TableCell>
                            <TableCell className="w-28 px-2 py-1 text-center whitespace-nowrap">
                                {renderSlotContent({
                                    badgeVariant,
                                    slotNumber,
                                    slotStatus: substitute?.substituteSlotStatus,
                                })}
                            </TableCell>
                            <TableCell className="w-20 px-2 py-1 pr-3 text-center whitespace-nowrap">
                                {renderOnHandUnits(substitute?.substituteOnHandUnits)}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

const renderActionsCell = ({ row, table }) => {
    const onToggleEnabled = table.options.meta?.onToggleEnabled;
    const onDelete = table.options.meta?.onDelete;
    const facilityName = table.options.meta?.facilityName;
    const rule = row.original;
    const isEnabled = rule?.isEnabled !== false;

    return (
        <SubstitutionRuleActionsMenu
            facilityName={facilityName}
            isEnabled={isEnabled}
            onToggleEnabled={() => onToggleEnabled?.(rule)}
            onDelete={() => onDelete?.(rule)}
        />
    );
};

const MemoizedSubstitutionRuleRow = React.memo(({ row }) => (
    <TableRow>
        {row.getVisibleCells().map((cell) => (
            <TableCell
                key={cell.id}
                className={cn('whitespace-normal break-words text-left', cell.column.columnDef.meta?.className)}
            >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </TableCell>
        ))}
    </TableRow>
));

MemoizedSubstitutionRuleRow.displayName = 'MemoizedSubstitutionRuleRow';

/**
 * @param {Object} props
 * @param {Array<Record<string, unknown>>} [props.data=[]]
 * @param {boolean} [props.isLoading=false]
 * @param {boolean} [props.isFetching=false]
 * @param {string} [props.emptyMessage='No substitution rules found.']
 * @param {string} [props.facilityName]
 * @param {(rule: Record<string, unknown>) => void} [props.onToggleEnabled]
 * @param {(rule: Record<string, unknown>) => void} [props.onDelete]
 */
export function SubstitutionRulesTable({
    data = [],
    emptyMessage = 'No substitution rules found.',
    facilityName,
    isFetching = false,
    isLoading = false,
    onDelete,
    onToggleEnabled,
}) {
    const tableData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    const columns = useMemo(
        () => [
            {
                accessorKey: 'ruleGroupId',
                header: 'Rule Group ID',
                meta: { className: 'w-[7%]' },
            },
            {
                accessorKey: 'isEnabled',
                cell: renderEnabledStatusCell,
                header: 'Enabled',
                meta: { className: 'w-[6%]' },
            },
            {
                accessorKey: 'originalExternalSku',
                header: 'Original External SKU',
                meta: { className: 'w-[14%]' },
            },
            {
                accessorKey: 'originalName',
                header: 'Original Name',
                meta: { className: 'w-[16%]' },
            },
            {
                id: 'originalSlot',
                cell: renderOriginalSlotCell,
                header: 'Slot',
                meta: { className: 'w-[8%]' },
            },
            {
                accessorKey: 'originalOnHandUnits',
                cell: ({ getValue }) => renderOnHandUnits(getValue()),
                header: 'On Hand',
                meta: { className: 'w-[6%]' },
            },
            {
                accessorKey: 'substitutionRuleQuantitySet',
                cell: renderSubstitutesCell,
                header: 'Substitutes',
                meta: { className: 'w-[35%]' },
            },
            {
                id: 'actions',
                header: 'Actions',
                cell: renderActionsCell,
                meta: { className: 'w-[8%]' },
            },
        ],
        [],
    );

    const tableMeta = useMemo(
        () => ({
            facilityName,
            onDelete,
            onToggleEnabled,
        }),
        [facilityName, onDelete, onToggleEnabled],
    );

    const table = useReactTable({
        columns,
        data: tableData,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row, index) => (row?.id != null ? String(row.id) : String(index)),
        meta: tableMeta,
    });

    return (
        <div className="flex flex-1 flex-col">
            {isFetching && !isLoading ? (
                <div className="mb-2 h-1 w-full overflow-hidden rounded bg-muted">
                    <div className="h-full w-1/3 animate-pulse bg-primary/40" />
                </div>
            ) : null}
            <div className="flex min-h-[240px] flex-1 flex-col">
                {isLoading ? (
                    <div className="flex flex-1 items-center justify-center p-6">
                        <div className="flex flex-col items-center gap-3">
                            <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">Loading substitution rules…</span>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-md border pb-2">
                        <Table className="w-full [&_table]:table-fixed">
                            <TableHeader>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableHead
                                                key={header.id}
                                                className={cn(
                                                    'whitespace-normal break-words text-left',
                                                    header.column.columnDef.meta?.className,
                                                )}
                                            >
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                            </TableHead>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHeader>
                            <TableBody>
                                {table.getRowModel().rows.length ? (
                                    table
                                        .getRowModel()
                                        .rows.map((row) => <MemoizedSubstitutionRuleRow key={row.id} row={row} />)
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={columns.length} className="h-24 text-center">
                                            {emptyMessage}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}

SubstitutionRulesTable.displayName = 'SubstitutionRulesTable';
